import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { Box, Flex, Text } from '@theme-ui/components';
import { css, useTheme } from '@emotion/react';

import { fadeIn } from '@app/styles/animations';
import type { AppTheme } from '@app/styles/theme';
import theme from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import LanguageProvider from '../../context/LanguageProvider';
import { getButtonStyles, UnstyledButton } from '../Button';
import Link from '../Link';
import messages from './LanguageSuggestPopup.messages';

interface LanguageSuggestPopupProps {
  preferredLanguage: string;
  onClose: () => void;
}

const LanguageSuggestPopup = ({
  preferredLanguage,
  onClose,
}: LanguageSuggestPopupProps) => {
  const { path, params } = useRouteMatch<{ url: string }>();
  const [show, setShow] = useState(true);
  const theme = useTheme();

  if (!show) {
    return null;
  }

  const closePopUp = (): void => {
    setShow(false);
    onClose();
  };

  return (
    <LanguageProvider
      locale={preferredLanguage}
      keys={[
        'components.languageSuggest.head',
        'components.languageSuggest.body',
        'components.languageSuggest.linkText',
      ]}
    >
      <Box css={popupStyles} p={3}>
        <Flex
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Text sx={{ fontSize: rem(18), fontWeight: 500, lineHeight: 1.15 }}>
            <FormattedMessage {...messages.head} />
          </Text>

          <UnstyledButton
            onClick={closePopUp}
            css={css`
              cursor: pointer;
            `}
          >
            <svg
              height="15"
              viewBox="0 0 15 15"
              width="15"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m1.42857143.70710678-.72146465.72146465 6.07142857 6.07142857-6.07142857 6.0714286.72146465.7214646 6.07142857-6.07142855 6.0714286 6.07142855.7214646-.7214646-6.07142855-6.0714286 6.07142855-6.07142857-.7214646-.72146465-6.0714286 6.07142857z"
                fill={theme.colors.labelGray}
              />
            </svg>
          </UnstyledButton>
        </Flex>

        <Box my={3}>
          <FormattedMessage {...messages.body} />
        </Box>

        <Link
          css={(theme: AppTheme) => getButtonStyles({ type: 'primary', theme })}
          to={path}
          params={{ url: params.url, language: preferredLanguage }}
          onClick={closePopUp}
        >
          <FormattedMessage {...messages.linkText} />
        </Link>
      </Box>
    </LanguageProvider>
  );
};

const popupStyles = css`
  position: fixed;
  right: 2rem;
  top: 6rem;
  width: 13rem;
  background: ${theme.colors.white};
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  z-index: 4;
  animation: ${fadeIn} 5s;
  font-size: ${rem(14)};
  line-height: 1.5;
`;

export default LanguageSuggestPopup;
